import React, { useEffect, useState } from "react";
import CollapsibleSection from "./CollapsibleSection";
import {
  SUPPORTED_CURRENCIES,
  formatCurrencyAmount,
  getExchangeRate,
  normalizeCurrencyCode,
  type SupportedCurrency,
} from "../../utils/currency";

type CurrencyConverterProps = {
  amount?: number | string;
  baseCurrency?: string;
  title?: string;
  /** Folds the converter away so it does not dominate a modal. */
  collapsible?: boolean;
  defaultOpen?: boolean;
};

function getAlternateCurrency(baseCurrency: SupportedCurrency) {
  return SUPPORTED_CURRENCIES.find((currency) => currency !== baseCurrency);
}

export default function CurrencyConverter({
  amount,
  baseCurrency = "USD",
  title = "Live Currency Converter",
  collapsible = false,
  defaultOpen = false,
}: CurrencyConverterProps) {
  const normalizedDefaultCurrency = normalizeCurrencyCode(baseCurrency);
  const [inputAmount, setInputAmount] = useState(
    amount === undefined || amount === null ? "" : String(amount),
  );
  const [sourceCurrency, setSourceCurrency] = useState<SupportedCurrency>(
    normalizedDefaultCurrency,
  );
  const [targetCurrency, setTargetCurrency] = useState<SupportedCurrency>(
    getAlternateCurrency(normalizedDefaultCurrency) ?? normalizedDefaultCurrency,
  );
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [rateDate, setRateDate] = useState("");
  const [rateSource, setRateSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  const parsedAmount = Number(inputAmount);
  const hasValidAmount =
    Boolean(inputAmount) && !Number.isNaN(parsedAmount) && parsedAmount > 0;

  useEffect(() => {
    setInputAmount(amount === undefined || amount === null ? "" : String(amount));
  }, [amount]);

  useEffect(() => {
    setSourceCurrency(normalizedDefaultCurrency);
    setTargetCurrency(
      getAlternateCurrency(normalizedDefaultCurrency) ?? normalizedDefaultCurrency,
    );
  }, [normalizedDefaultCurrency]);

  useEffect(() => {
    if (sourceCurrency === targetCurrency) {
      const alternateCurrency = getAlternateCurrency(sourceCurrency);

      if (alternateCurrency) {
        setTargetCurrency(alternateCurrency);
      }
    }
  }, [sourceCurrency, targetCurrency]);

  useEffect(() => {
    if (!hasValidAmount) {
      setConvertedAmount(null);
      setRate(null);
      setRateDate("");
      setRateSource("");
      setError("");
      setLoading(false);
      return;
    }

    let ignore = false;

    setLoading(true);
    setError("");

    getExchangeRate(sourceCurrency, targetCurrency)
      .then((exchangeRate) => {
        if (ignore) {
          return;
        }

        setRate(exchangeRate.rate);
        setRateDate(exchangeRate.date);
        setRateSource(exchangeRate.source);
        setConvertedAmount(parsedAmount * exchangeRate.rate);
      })
      .catch((fetchError) => {
        if (ignore) {
          return;
        }

        console.log("Error fetching exchange rate", fetchError);
        setConvertedAmount(null);
        setRate(null);
        setRateDate("");
        setRateSource("");
        setError("Live rate unavailable right now");
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [hasValidAmount, parsedAmount, sourceCurrency, targetCurrency, retryCount]);

  const targetCurrencyOptions = SUPPORTED_CURRENCIES.filter(
    (currency) => currency !== sourceCurrency,
  );

  const converterBody = (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <div className="mb-1 text-[12px] font-medium text-[#211f26]">
            Amount
          </div>
          <input
            value={inputAmount}
            onChange={(event) => {
              const nextValue = event.target.value;

              if (nextValue === "" || /^\d*\.?\d*$/.test(nextValue)) {
                setInputAmount(nextValue);
              }
            }}
            placeholder="Enter amount"
            inputMode="decimal"
            className="h-11 w-full rounded-xl border border-[#eae7ec] bg-white px-3 text-[14px] text-[#211f26] outline-none"
          />
        </div>

        <div>
          <div className="mb-1 text-[12px] font-medium text-[#211f26]">From</div>
          <select
            value={sourceCurrency}
            onChange={(event) =>
              setSourceCurrency(normalizeCurrencyCode(event.target.value))
            }
            className="h-11 w-full rounded-xl border border-[#eae7ec] bg-white px-3 text-[13px] text-[#211f26] outline-none"
          >
            {SUPPORTED_CURRENCIES.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="mb-1 text-[12px] font-medium text-[#211f26]">To</div>
          <select
            value={targetCurrency}
            onChange={(event) =>
              setTargetCurrency(normalizeCurrencyCode(event.target.value))
            }
            className="h-11 w-full rounded-xl border border-[#eae7ec] bg-white px-3 text-[13px] text-[#211f26] outline-none"
          >
            {targetCurrencyOptions.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 text-[13px] leading-[20px] text-[#605d66]">
        {!hasValidAmount && "Enter an amount to see the latest conversion."}
        {loading && "Fetching latest rate..."}
        {!loading && error && (
          <div className="flex flex-wrap items-center gap-2">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setRetryCount((count) => count + 1)}
              className="rounded-lg border border-[#eae7ec] bg-white px-2 py-1 text-[12px] font-medium text-[#211f26]"
            >
              Retry
            </button>
          </div>
        )}
        {!loading && !error && convertedAmount !== null && rate !== null && (
          <div className="space-y-1">
            <div className="text-[15px] font-medium text-[#211f26]">
              {formatCurrencyAmount(parsedAmount, sourceCurrency)} ={" "}
              {formatCurrencyAmount(convertedAmount, targetCurrency)}
            </div>
            <div>
              1 {sourceCurrency} ={" "}
              {formatCurrencyAmount(rate, targetCurrency)}
              {rateDate ? ` on ${rateDate}` : ""}
              {rateSource ? ` via ${rateSource}` : ""}
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (collapsible) {
    return (
      <div className="mt-4">
        <CollapsibleSection
          title={title}
          subtitle="Uses the latest available market rate."
          defaultOpen={defaultOpen}
        >
          {converterBody}
        </CollapsibleSection>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-[#eae7ec] bg-[#faf9fb] p-4">
      <div className="flex flex-row items-start justify-between gap-4">
        <div>
          <div className="text-[14px] font-medium text-[#211f26]">{title}</div>
          <div className="mt-1 text-[12px] leading-[18px] text-[#605d66]">
            Uses the latest available market rate.
          </div>
        </div>
      </div>
      <div className="mt-4">{converterBody}</div>
    </div>
  );
}
